
import * as React from 'react';
import { Delete, ReplacePatch } from '../../utilities/Graft';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';
import { LayoutTypeFacet } from '../../models/layouts/LayoutTypeFacet';
import { createRelay, IRelayReceiverStateProps } from '../../utilities/components/Relay';
import { renderChildrenWithProps, StatePropsUpdater } from '../../utilities/components/RenderProps';
import { mapObject } from '../../utilities/objects';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { createMemoizer } from '../../utilities/Memoizer';

const layoutManagerKeyFacet = new AutoKeyFacet('layoutManager');

/**
 * @internal
 */
export interface ILayoutState {
  facet: LayoutTypeFacet;
  isAvailable: boolean;
  content: JSX.Element;
}

/**
 * @internal
 */
export interface ILayoutManagerStateProps {
  layouts: {
    [key: string]: ILayoutState;
  };
}

/**
 * @internal
 */
export interface ILayoutManagerProps {
  /**
   * Renders content in-band with the rolled-up set of layouts and their states.
   */
  withLayouts?: (props: ILayoutManagerStateProps) => JSX.Element | null;
  /**
   * Receives an out-of-band callback with the rolled-up set of layouts and their states.
   */
  onUpdateLayouts?: (layouts: {
    [key: string]: ILayoutState;
  }) => void;
}

interface ILayoutManagerRelayState extends ILayoutManagerStateProps {
  children: {
    [id: string]: ILayoutManagerStateProps;
  };
}

const INITIAL_LAYOUT_MANAGER_STATE: ILayoutManagerRelayState = {
  children: {},
  layouts: {}
};

const LAYOUT_MANAGER_RELAY = createRelay<ILayoutManagerRelayState>(INITIAL_LAYOUT_MANAGER_STATE, {
  merge: (current: ILayoutManagerRelayState) => {
    return {
      children: mapObject(current.children, (child: ILayoutManagerStateProps) => {
        return new ReplacePatch(child);
      }),
      layouts: mapObject(current.layouts, (layout: ILayoutState) => {
        return {
          ...layout,
          content: new ReplacePatch(layout.content)
        };
      })
    };
  },
  remove: (current: ILayoutManagerRelayState) => {
    return {
      children: mapObject(current.children, () => {
        return new Delete<ILayoutManagerStateProps>();
      }),
      layouts: mapObject(current.layouts, () => {
        return new Delete<ILayoutState>();
      })
    };
  },
  allowSenderOutsideReceiver: true
});

const callbackMemoizer = createMemoizer((onUpdateLayouts: ((layouts: {
  [key: string]: ILayoutState;
}) => void)): ((stateProps: ILayoutManagerStateProps) => void) => {
  return ({ layouts }: ILayoutManagerStateProps) => {
    onUpdateLayouts(layouts);
  };
});

const mergeMemoizer = createMemoizer(mergeLayouts);

/**
 * @internal
 */
export class LayoutManager extends React.PureComponent<ILayoutManagerProps> {
  private _id: string;

  constructor(props: ILayoutManagerProps) {
    super(props);

    this._id = layoutManagerKeyFacet.serializeNext();
  }

  public render(): JSX.Element | null {
    const {
      withLayouts,
      children,
      onUpdateLayouts
    } = this.props;

    const onUpdate = onUpdateLayouts ? callbackMemoizer(onUpdateLayouts) : undefined;

    return (
      <LAYOUT_MANAGER_RELAY.Receiver
        values={ {
          '': children ? (
            <>{ children }</>
          ) : null
        } }
      >
        { ({ values }: IRelayReceiverStateProps<ILayoutManagerRelayState>) => {
          const {
            '': state = INITIAL_LAYOUT_MANAGER_STATE
          } = values;

          const outputLayouts = mergeMemoizer(state);

          return (
            <>
              {
                onUpdate ? (
                  <StatePropsUpdater stateProps={ outputLayouts } onUpdate={ onUpdate } />
                ) : null
              }
              <LAYOUT_MANAGER_RELAY.Sender
                children={ {
                  [this._id]: outputLayouts
                } }
                layouts={ {} }
              />
              { renderChildrenWithProps(withLayouts, outputLayouts) }
            </>
          );
        } }
      </LAYOUT_MANAGER_RELAY.Receiver>
    );
  }
}

function mergeLayouts(state: ILayoutManagerRelayState): {
  layouts: {
    [key: string]: ILayoutState;
  };
} {
  const layouts: {
    [key: string]: ILayoutState;
  } = {};

  for (const id in state.children) {
    if (Object.prototype.hasOwnProperty.call(state.children, id)) {
      const childLayouts = state.children[id];

      for (const key in childLayouts.layouts) {
        if (Object.prototype.hasOwnProperty.call(childLayouts.layouts, key)) {
          layouts[key] = childLayouts.layouts[key];
        }
      }
    }
  }

  for (const key in state.layouts) {
    if (Object.prototype.hasOwnProperty.call(state.layouts, key)) {
      layouts[key] = state.layouts[key];
    }
  }

  return {
    layouts
  };
}

/**
 * @public
 */
export interface ILayoutProps {
  isAvailable?: boolean;
}

/**
 * @public
 */
export type IDecorateAsLayoutProps<TProps> = IDecoratedProps<TProps, {}, ILayoutProps>;

/**
 * Wraps a component and makes it available as a registered layout.
 * @public
 */
export function asLayout(
  layoutType: LayoutTypeFacet
): IComponentDecorator<{}, ILayoutProps> {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, {}, ILayoutProps>> => {
    const Component = component;
    const key = layoutType.id;

    type IProps = IDecoratedProps<P, {}, ILayoutProps>;

    class Layout extends React.PureComponent<IProps> {

      public render(): JSX.Element | null {
        const {
          isAvailable = true,
          ...contentProps
        } = this.props as ILayoutProps;

        const content = (
          <Component { ...contentProps as P } />
        );

        return (
          <LAYOUT_MANAGER_RELAY.Sender
            children={ {} }
            layouts={ {
              [key]: {
                facet: layoutType,
                isAvailable,
                content
              }
            } }
          />
        );
      }
    }

    return Layout;
  };
}
